import ButtonCommon from 'components/common/ButtonCommon';
import SizeBox from 'components/common/SizeBox';
import {
  COUNTRY_FLAG_REFERENCES,
  ORDER_EMAIL,
  TRANSACTION_ADD_PREFIX,
} from 'constants';
import { format } from 'helpers';
import Cookies from 'helpers/cookies';
import { MAKE_PAYMENT_KEY } from 'hooks/form-add-transaction/useAddTransaction';
import { FX_MAKE_PAYMENT_KEY } from 'hooks/fx/useFxAddTransaction';
import useLang from 'hooks/useLang';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { paragraphMixin } from 'styles';

const ExchangeRateItem = ({
  country,
  countryName,
  rates,
  isActive,
  onClick,
}) => {
  const { t } = useLang();
  const navigate = useNavigate();

  const handleOnClick = () => {
    Cookies.remove(MAKE_PAYMENT_KEY);
    Cookies.remove(FX_MAKE_PAYMENT_KEY);
    Cookies.remove(ORDER_EMAIL);
    navigate(TRANSACTION_ADD_PREFIX);
  };

  const getReceivedCurrenciesLabel = (num) => {
    if (isActive) return t('label_receiving_currency');
    if (num === 1) {
      return t('label_received_currency', {
        number: num,
      });
    }
    return t('label_received_currencies', {
      number: num,
    });
  };

  const renderCurrencies = (rates, optional = {}) => {
    const { formattedRateStyle } = optional || {};
    if (isEmpty(rates))
      return <NoRates>{t('label_no_rates_available')}</NoRates>;
    return (
      <>
        {Object.keys(rates).map((currencyCode) => {
          const rateInfo = rates[currencyCode];
          if (
            rateInfo.rate &&
            typeof rateInfo.rate === 'number' &&
            !isNaN(rateInfo.rate)
          ) {
            const formattedRate = format.toAmountStr2(rateInfo.rate);
            return (
              <div key={currencyCode}>
                <span>{rateInfo.currencyCode}</span>
                <br />
                <SizeBox height={4} />
                <span className="rate" style={formattedRateStyle || {}}>
                  {formattedRate}
                </span>
              </div>
            );
          }
          return null;
        })}
      </>
    );
  };

  if (isEmpty(rates)) return null;
  if (isActive) {
    return (
      <ItemWrap $isActive={isActive} onClick={onClick}>
        <CountryName $isActive={isActive}>
          <FlagIcon
            src={COUNTRY_FLAG_REFERENCES[country]}
            $isActive={isActive}
          />
          {countryName}
        </CountryName>
        <SizeBox height={4} />
        <Desc>{getReceivedCurrenciesLabel(Object.keys(rates)?.length)}</Desc>
        <Desc style={{ fontSize: '12px' }}>
          ({t('label_exchange_rate_per_aud')})
        </Desc>
        <CurrenciesWrap>
          <Currencies $isActive={isActive}>
            {renderCurrencies(rates)}
          </Currencies>
        </CurrenciesWrap>
        <ButtonCommon
          value={t('button_send_now_2')}
          color="var(--ds-c-blue)"
          background="var(--ds-c-white)"
          styles={{
            height: '44px',
            width: '150px',
            position: 'absolute',
            bottom: '10px',
            right: '10px',
          }}
          onClick={handleOnClick}
        />
      </ItemWrap>
    );
  }
  return (
    <ItemWrap onClick={onClick}>
      <CountryName>
        <FlagIcon src={COUNTRY_FLAG_REFERENCES[country]} />
        {countryName}
      </CountryName>
      {getReceivedCurrenciesLabel(Object.keys(rates)?.length)}
      <Currencies>
        {Object.keys(rates).map((currencyCode) => {
          const rateInfo = rates[currencyCode];
          return <span key={currencyCode}>{rateInfo.currencyCode}</span>;
        })}
      </Currencies>
      <Link>{t('link_view_details')}</Link>
    </ItemWrap>
  );
};

const ItemWrap = styled.div`
  position: relative;
  padding: 18px 8px 20px 8px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;

  border-radius: 10px;
  background: var(--ds-bg-4);
  border: none;
  color: var(--ds-c-black);
  cursor: pointer;
  user-select: none;

  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 15px;
  text-align: left;
  min-width: calc((100% - 343px - 80px) / 4);
  height: 212px;

  ${(props) =>
    props.$isActive
      ? `
        flex: 2;
        background: #081933;
        color: var(--ds-c-white);
        padding: 10px;
        box-shadow: 0px 4px 15px 0px #0000001A;
        min-width: 343px;
        height: 212px;
        cursor: default;
    `
      : `
        align-items: stretch;
        position: relative;
    `};
`;

const CountryName = styled.h4`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  font-family: var(--ff-primary);
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  text-transform: uppercase;
  margin: 0;

  ${(props) =>
    props.$isActive
      ? `
        flex-direction: row;
        align-items: center;
        color: #fff;
        font-weight: 500;
      `
      : `
        flex: 1;
        margin-bottom: 12px;
      `};
`;

const FlagIcon = styled.img`
  width: 30px;
  height: 30px;
  object-fit: cover;
  border: 1px solid var(--ds-c-black);
  border-radius: 50%;

  ${(props) =>
    props.$isActive
      ? `
        width: 30px;
        height: 30px;
    `
      : ``};
`;

const CurrenciesWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  height: fit-content;
`;

const Currencies = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 4px;
  margin: 8px 0 0 0;
  font-weight: 700;
  width: 100%;

  & span {
    background: #fff;
    border: 1px solid var(--ds-c-black);
    border-radius: 10px;
    padding: 0 5px;
    line-height: 20px;
  }

  ${(props) =>
    props.$isActive
      ? `
      flex-direction: row;
      justify-content: flex-start;
      width: 100%;
      font-size: 16px;
      gap: 0px;
      margin: 20px 0 0 0;
      
      & div {
        width: 33.33%;
      }

      & span {
        border: none;
        background: transparent;
        color: var(--c-quaternary);
        height: fit-content;
        line-height: 100%;
        font-size: 14px;
        font-weight: 500;
        text-transform: uppercase;
        padding: 0;
        margin-bottom: 2px;
      }
      & span.rate {
        font-size: 24px;
        color: #fff;
      }
      `
      : `
      margin-bottom: 26px;
    `};
`;

const Link = styled.div`
  width: 100%;
  color: var(--ds-c-blue-hyperlink-default);
  text-align: center;
`;

const Desc = styled.p`
  ${paragraphMixin};
  margin: 0;
  font-weight: 500;
  font-size: 14px;
  line-height: 17px;
  color: #fff;
`;

const NoRates = styled.div`
  ${paragraphMixin};
  font-weight: 500;
  width: 100% !important;
  text-align: center;
  color: #fff;
`;

export default ExchangeRateItem;
